<script lang="ts" setup>
import { Link, Moon, Refresh, Setting, Sunny } from "@element-plus/icons-vue"
import { ElMessageBox } from "element-plus"
import { type Component, computed, nextTick, ref } from "vue"
import { type BusinessProductCardViewMode, type BusinessTableCardDensity, type ThemePreference, type ThemeProfileName, useTheme } from "@/hooks/useTheme"
import { runWithViewTransition } from "@/utils/theme/viewTransition"

const {
  themeList,
  themeFontSizeList,
  themeFontWeightList,
  themeProfileList,
  themeDensityScaleList,
  themeSurfaceEffectList,
  businessTableCardDensityList,
  businessProductCardViewModeList,
  themeColorPresets,
  themeColorPresetValues,
  activeThemeName,
  resolvedThemeName,
  activeThemeProfile,
  activeThemeColor,
  activeThemeFontSize,
  activeThemeFontWeight,
  activeThemeDensityScale,
  activeThemeSurfaceEffect,
  activeThemeBrightness,
  activeThemeBrightnessEnabled,
  activeThemePerformanceMode,
  activeThemeAdvancedControls,
  activeBusinessAllCategoryFlatMode,
  activeBusinessTableCardDensity,
  activeBusinessProductCardViewMode,
  activeBusinessTableCardDensityLinked,
  activeBusinessProductCardDensityLinked,
  setThemeProfile,
  setTheme,
  setThemeColor,
  setThemeFontSize,
  setThemeFontWeight,
  setThemeDensityScale,
  setThemeSurfaceEffect,
  setThemeBrightness,
  setThemeBrightnessEnabled,
  setThemePerformanceMode,
  setThemeAdvancedControls,
  setBusinessAllCategoryFlatMode,
  setBusinessTableCardDensity,
  setBusinessProductCardViewMode,
  setBusinessTableCardDensityLinked,
  setBusinessProductCardDensityLinked,
  resetThemeSettings,
} = useTheme()

const themeIconMap: Record<ThemePreference, Component> = {
  normal: Sunny,
  dark: Moon,
  system: Setting,
}

const themePanelVisible = ref(false)
const brightnessDialogKeepingPanelVisible = ref(false)
let brightnessDialogRestoreTimer: ReturnType<typeof setTimeout> | null = null

const activeThemeTitle = computed(() => {
  return themeList.find(theme => theme.name === activeThemeName.value)?.title || "主题"
})

const activeThemeProfileTitle = computed(() => {
  return themeProfileList.find(profile => profile.name === activeThemeProfile.value)?.title || "默认"
})

const activeThemeBrightnessFilterActive = computed(() => {
  return !activeThemePerformanceMode.value && activeThemeBrightnessEnabled.value && activeThemeBrightness.value !== 100
})

const activeThemeBrightnessStatus = computed(() => {
  if (activeThemePerformanceMode.value) {
    return activeThemeBrightnessEnabled.value ? `${activeThemeBrightness.value}% / 性能模式` : `${activeThemeBrightness.value}% / 已关闭`
  }
  if (activeThemeBrightnessFilterActive.value) {
    return `${activeThemeBrightness.value}%`
  }
  return activeThemeBrightnessEnabled.value ? `${activeThemeBrightness.value}% / 未应用` : `${activeThemeBrightness.value}% / 已关闭`
})

const activeThemePerformanceModeStatus = computed(() => {
  return activeThemePerformanceMode.value ? "丝滑优先" : "视觉优先"
})

const activeThemeDescription = computed(() => {
  const profileText = `主题方案 ${activeThemeProfileTitle.value}`
  const performanceText = `性能模式 ${activeThemePerformanceModeStatus.value}`
  const colorText = `主题色 ${activeThemeColor.value}`
  const surfaceText = `背景 ${activeThemeSurfaceEffectTitle.value}`
  const brightnessText = `亮度 ${activeThemeBrightnessStatus.value}`
  const fontText = `字号 ${activeThemeFontSize.value}px，字重 ${activeThemeFontWeight.value}`
  const densityText = `紧凑性 ${activeThemeDensityTitle.value}`
  const businessText = `营业视图 ${activeBusinessDisplaySummary.value}`
  if (activeThemeName.value === "system") {
    const resolvedThemeTitle = themeList.find(theme => theme.name === resolvedThemeName.value)?.title || "默认"
    return `${profileText}，${activeThemeTitle.value}，当前为${resolvedThemeTitle}，${performanceText}，${colorText}，${surfaceText}，${brightnessText}，${fontText}，${densityText}，${businessText}`
  }
  return `${profileText}，${activeThemeTitle.value}，${performanceText}，${colorText}，${surfaceText}，${brightnessText}，${fontText}，${densityText}，${businessText}`
})

const activeThemeIcon = computed(() => themeIconMap[activeThemeName.value])

const activeThemeDensityTitle = computed(() => {
  return themeDensityScaleList.find(density => density.value === activeThemeDensityScale.value)?.title || "标准"
})

const activeThemeDensityDescription = computed(() => {
  return themeDensityScaleList.find(density => density.value === activeThemeDensityScale.value)?.description || "页面 20px / 控件 40px"
})

const activeThemeSurfaceEffectTitle = computed(() => {
  return themeSurfaceEffectList.find(effect => effect.name === activeThemeSurfaceEffect.value)?.title || "净面"
})

const activeBusinessTableCardDensityTitle = computed(() => {
  return businessTableCardDensityList.find(item => item.name === activeBusinessTableCardDensity.value)?.title || "标准"
})

const activeBusinessProductCardViewModeTitle = computed(() => {
  return businessProductCardViewModeList.find(item => item.name === activeBusinessProductCardViewMode.value)?.title || "标准"
})

const activeBusinessDisplaySummary = computed(() => {
  const allViewText = activeBusinessAllCategoryFlatMode.value ? "全部平铺" : "全部分组"
  return `${allViewText}，桌台${activeBusinessTableCardDensityTitle.value}，商品${activeBusinessProductCardViewModeTitle.value}`
})

const activeBusinessDensityAllLinked = computed(() => {
  return activeBusinessTableCardDensityLinked.value && activeBusinessProductCardDensityLinked.value
})

const getSliderMarks = (items: Array<{ value: number, title: string }>) => {
  return Object.fromEntries(items.map(item => [item.value, item.title]))
}

const themeFontSizeSliderMarks = computed(() => getSliderMarks(themeFontSizeList))
const themeFontWeightSliderMarks = computed(() => getSliderMarks(themeFontWeightList))
const themeDensitySliderMarks = computed(() => getSliderMarks(themeDensityScaleList))

const setTransitionOrigin = () => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return
  }
  const trigger = document.querySelector<HTMLElement>(".theme-switch-button")
  const rect = trigger?.getBoundingClientRect()
  const x = rect ? rect.left + rect.width / 2 : window.innerWidth - 40
  const y = rect ? rect.top + rect.height / 2 : 40
  const radius = Math.max(
    Math.sqrt(x * x + y * y),
    Math.sqrt((window.innerWidth - x) * (window.innerWidth - x) + y * y),
    Math.sqrt(x * x + (window.innerHeight - y) * (window.innerHeight - y)),
    Math.sqrt((window.innerWidth - x) * (window.innerWidth - x) + (window.innerHeight - y) * (window.innerHeight - y)),
  )

  document.documentElement.style.setProperty("--transition-x", `${x}px`)
  document.documentElement.style.setProperty("--transition-y", `${y}px`)
  document.documentElement.style.setProperty("--transition-radius", `${radius}px`)
}

const setThemeWithTransition = (themeName: ThemePreference) => {
  if (activeThemePerformanceMode.value) {
    setTheme(themeName)
    return
  }
  setTransitionOrigin()
  runWithViewTransition(() => {
    setTheme(themeName)
  })
}

const setThemeProfileWithTransition = (profileName: ThemeProfileName) => {
  if (activeThemePerformanceMode.value) {
    setThemeProfile(profileName)
    return
  }
  setTransitionOrigin()
  runWithViewTransition(() => {
    setThemeProfile(profileName)
  })
}

const handlePresetClick = (value: string) => {
  setThemeColor(value)
}

const handlePresetKeydown = (event: KeyboardEvent, value: string) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault()
    setThemeColor(value)
  }
}

const handleThemeChange = (value: string | number | boolean | undefined) => {
  if (typeof value === "string") {
    setThemeWithTransition(value as ThemePreference)
  }
}

const handleThemeProfileChange = (value: string | number | boolean | undefined) => {
  if (typeof value === "string") {
    setThemeProfileWithTransition(value as ThemeProfileName)
  }
}

const handleThemeBrightnessChange = (value: string | number | number[] | undefined) => {
  if (typeof value === "number") {
    setThemeBrightness(value)
  }
}

const restoreThemePanelVisible = async () => {
  await nextTick()
  brightnessDialogKeepingPanelVisible.value = true
  themePanelVisible.value = true
  if (brightnessDialogRestoreTimer) {
    clearTimeout(brightnessDialogRestoreTimer)
  }
  brightnessDialogRestoreTimer = setTimeout(() => {
    brightnessDialogKeepingPanelVisible.value = false
    brightnessDialogRestoreTimer = null
  }, 600)
}

const handleThemePanelVisibleChange = (visible: boolean) => {
  if (!visible && brightnessDialogKeepingPanelVisible.value) {
    themePanelVisible.value = true
    return
  }
  themePanelVisible.value = visible
}

const handleThemeBrightnessEnabledChange = async (value: string | number | boolean | undefined) => {
  const shouldEnableBrightness = value === true
  if (!shouldEnableBrightness) {
    setThemeBrightnessEnabled(false)
    return
  }
  brightnessDialogKeepingPanelVisible.value = true
  themePanelVisible.value = true
  try {
    await ElMessageBox.confirm(
      "开启后页面会整体变亮或变暗，部分电脑可能不够顺畅。<strong>建议优先调整显示器亮度</strong>，也可以切换到“黑金”或“护眼”主题。确认开启后会关闭性能模式。",
      "开启亮度调节",
      {
        confirmButtonText: "确认开启",
        cancelButtonText: "保持性能模式",
        dangerouslyUseHTMLString: true,
        type: "warning",
      },
    )
    setThemePerformanceMode(false)
    setThemeBrightnessEnabled(true)
  } catch {
    setThemeBrightnessEnabled(false)
  } finally {
    await restoreThemePanelVisible()
  }
}

const handleThemePerformanceModeChange = (value: string | number | boolean | undefined) => {
  setThemePerformanceMode(value === true)
}

const handleThemeAdvancedControlsChange = (value: string | number | boolean | undefined) => {
  setThemeAdvancedControls(value === true)
}

const handleThemeFontSizeChange = (value: string | number | number[] | undefined) => {
  if (typeof value === "number") {
    setThemeFontSize(value)
  }
}

const handleThemeFontWeightChange = (value: string | number | number[] | undefined) => {
  if (typeof value === "number") {
    setThemeFontWeight(value)
  }
}

const handleThemeDensityScaleChange = (value: string | number | number[] | undefined) => {
  if (typeof value === "number") {
    setThemeDensityScale(value)
  }
}

const handleThemeSurfaceEffectChange = (value: string | number | boolean | undefined) => {
  if (typeof value === "string") {
    setThemeSurfaceEffect(value as typeof activeThemeSurfaceEffect.value)
  }
}

const handleBusinessAllCategoryFlatModeChange = (value: string | number | boolean | undefined) => {
  setBusinessAllCategoryFlatMode(value === true)
}

const handleBusinessTableCardDensityChange = (value: string | number | boolean | undefined) => {
  if (typeof value === "string") {
    setBusinessTableCardDensity(value as BusinessTableCardDensity)
  }
}

const handleBusinessProductCardViewModeChange = (value: string | number | boolean | undefined) => {
  if (typeof value === "string") {
    setBusinessProductCardViewMode(value as BusinessProductCardViewMode)
  }
}

const handleToggleBusinessTableCardDensityLinked = () => {
  setBusinessTableCardDensityLinked(!activeBusinessTableCardDensityLinked.value)
}

const handleToggleBusinessProductCardDensityLinked = () => {
  setBusinessProductCardDensityLinked(!activeBusinessProductCardDensityLinked.value)
}

const handleToggleAllBusinessDensityLinked = () => {
  const nextLinked = !activeBusinessDensityAllLinked.value
  setBusinessTableCardDensityLinked(nextLinked)
  setBusinessProductCardDensityLinked(nextLinked)
}

const handleThemeColorChange = (value: string | null) => {
  setThemeColor(value)
}

const handleResetThemeSettings = () => {
  if (activeThemePerformanceMode.value) {
    resetThemeSettings()
    return
  }
  setTransitionOrigin()
  runWithViewTransition(() => {
    resetThemeSettings()
  })
}

const handleResetThemeBrightness = () => {
  setThemeBrightness(100)
}

const handleResetThemeFontSize = () => {
  setThemeFontSize(14)
}

const handleResetThemeFontWeight = () => {
  setThemeFontWeight(400)
}
</script>

<template>
  <span class="theme-switch-root">
    <el-popover
      :visible="themePanelVisible"
      trigger="click"
      placement="bottom-end"
      width="760"
      popper-class="theme-switch-popover"
      @update:visible="handleThemePanelVisibleChange"
    >
      <template #reference>
        <el-button
          class="theme-switch-button"
          text
          circle
          :aria-label="`打开主题设置，当前${activeThemeDescription}`"
          :title="activeThemeDescription"
        >
          <span class="theme-switch-button__swatch" :style="{ backgroundColor: activeThemeColor }" />
          <el-icon>
            <component :is="activeThemeIcon" />
          </el-icon>
        </el-button>
      </template>

      <div class="theme-panel">
        <div class="theme-panel__titlebar">
          <div>
            <div class="theme-panel__title">主题设置</div>
          </div>
          <el-button text size="small" @click="handleResetThemeSettings">
            <el-icon>
              <Refresh />
            </el-icon>
            <span>重置全部</span>
          </el-button>
        </div>

        <div class="theme-panel__columns">
          <div class="theme-panel__column">
            <section class="theme-panel__section theme-panel__section--profile">
              <div class="theme-panel__header">
                <span class="theme-panel__label">主题方案</span>
                <div class="theme-panel__header-actions">
                  <el-checkbox
                    class="theme-panel__performance-checkbox"
                    :model-value="activeThemePerformanceMode"
                    aria-label="启用性能模式"
                    @change="handleThemePerformanceModeChange"
                  >
                    性能模式
                  </el-checkbox>
                  <el-checkbox
                    class="theme-panel__advanced-checkbox"
                    :model-value="activeThemeAdvancedControls"
                    aria-label="显示高级调整"
                    @change="handleThemeAdvancedControlsChange"
                  >
                    高级调整
                  </el-checkbox>
                </div>
              </div>
              <el-radio-group
                class="theme-panel__profile-group"
                :model-value="activeThemeProfile"
                @change="handleThemeProfileChange"
              >
                <el-radio-button
                  v-for="profile in themeProfileList"
                  :key="profile.name"
                  :value="profile.name"
                >
                  <span class="theme-panel__profile-option">
                    {{ profile.title }}
                  </span>
                </el-radio-button>
              </el-radio-group>
            </section>

            <template v-if="activeThemeAdvancedControls">
              <section class="theme-panel__section theme-panel__section--brightness">
                <div class="theme-panel__header">
                  <span class="theme-panel__label-group">
                    <span class="theme-panel__label">亮度</span>
                    <el-switch
                      class="theme-panel__brightness-switch"
                      :model-value="activeThemeBrightnessEnabled"
                      inline-prompt
                      inactive-text="关"
                      active-text="开"
                      aria-label="启用亮度调节"
                      @change="handleThemeBrightnessEnabledChange"
                    />
                  </span>
                  <span class="theme-panel__value">{{ activeThemeBrightnessStatus }}</span>
                </div>
                <div class="theme-panel__brightness-control theme-panel__metric-control">
                  <el-slider
                    class="theme-panel__slider theme-panel__slider--brightness"
                    :model-value="activeThemeBrightness"
                    :disabled="activeThemePerformanceMode || !activeThemeBrightnessEnabled"
                    :min="70"
                    :max="130"
                    :step="1"
                    :show-tooltip="true"
                    @input="handleThemeBrightnessChange"
                    @change="handleThemeBrightnessChange"
                  />
                  <el-tooltip content="重置亮度" placement="top">
                    <el-button
                      class="theme-panel__reset-button"
                      text
                      circle
                      aria-label="重置亮度"
                      @click="handleResetThemeBrightness"
                    >
                      <el-icon><Refresh /></el-icon>
                    </el-button>
                  </el-tooltip>
                </div>
              </section>

              <section class="theme-panel__section">
                <div class="theme-panel__header">
                  <span class="theme-panel__label">外观模式</span>
                  <span class="theme-panel__value">{{ activeThemeTitle }}</span>
                </div>
                <el-radio-group
                  class="theme-panel__segmented-group"
                  :model-value="activeThemeName"
                  size="small"
                  @change="handleThemeChange"
                >
                  <el-radio-button
                    v-for="theme in themeList"
                    :key="theme.name"
                    :value="theme.name"
                  >
                    <span class="theme-panel__mode">
                      <el-icon>
                        <component :is="themeIconMap[theme.name]" />
                      </el-icon>
                      <span>{{ theme.title }}</span>
                    </span>
                  </el-radio-button>
                </el-radio-group>
              </section>

              <section class="theme-panel__section theme-panel__section--color">
                <div class="theme-panel__header theme-panel__header--color">
                  <span class="theme-panel__label">主题色</span>
                  <div class="theme-panel__scale theme-panel__scale--inline" aria-hidden="true">
                    <span class="theme-panel__scale-item theme-panel__scale-item--page" />
                    <span class="theme-panel__scale-item theme-panel__scale-item--surface" />
                    <span class="theme-panel__scale-item theme-panel__scale-item--soft" />
                    <span class="theme-panel__scale-item theme-panel__scale-item--main" />
                    <span class="theme-panel__scale-item theme-panel__scale-item--hover" />
                    <span class="theme-panel__scale-item theme-panel__scale-item--active" />
                  </div>
                  <span class="theme-panel__value">{{ activeThemeColor }}</span>
                </div>
                <div class="theme-panel__color-row">
                  <el-color-picker
                    class="theme-panel__color-picker"
                    :model-value="activeThemeColor"
                    :predefine="themeColorPresetValues"
                    color-format="hex"
                    size="large"
                    @active-change="handleThemeColorChange"
                    @change="handleThemeColorChange"
                  />
                  <div class="theme-panel__color-preview">
                    <button
                      v-for="item in themeColorPresets"
                      :key="item.value"
                      class="theme-panel__preset"
                      :class="{ 'is-active': activeThemeColor === item.value }"
                      type="button"
                      :aria-label="`选择${item.title} ${item.value}`"
                      @click="handlePresetClick(item.value)"
                      @keydown="handlePresetKeydown($event, item.value)"
                    >
                      <span
                        class="theme-panel__preset-swatch"
                        :style="{ backgroundColor: item.value }"
                      />
                      <span class="theme-panel__preset-name">{{ item.title }}</span>
                    </button>
                  </div>
                </div>
              </section>

              <section class="theme-panel__section theme-panel__section--surface">
                <div class="theme-panel__header">
                  <span class="theme-panel__label">背景</span>
                  <span class="theme-panel__value">{{ activeThemeSurfaceEffectTitle }}</span>
                </div>
                <el-radio-group
                  class="theme-panel__surface-group"
                  :model-value="activeThemeSurfaceEffect"
                  size="small"
                  @change="handleThemeSurfaceEffectChange"
                >
                  <el-radio-button
                    v-for="effect in themeSurfaceEffectList"
                    :key="effect.name"
                    :value="effect.name"
                  >
                    <span class="theme-panel__surface-option">
                      <span>{{ effect.title }}</span>
                      <span>{{ effect.description }}</span>
                    </span>
                  </el-radio-button>
                </el-radio-group>
              </section>
            </template>

            <section class="theme-panel__section theme-panel__section--font">
              <div class="theme-panel__header">
                <span class="theme-panel__label">字体</span>
                <span class="theme-panel__value">{{ activeThemeFontSize }}px / {{ activeThemeFontWeight }}</span>
              </div>
              <div class="theme-panel__slider-field">
                <div class="theme-panel__slider-header">
                  <span>字号基准</span>
                  <span>{{ activeThemeFontSize }}px</span>
                </div>
                <div class="theme-panel__metric-control">
                  <el-slider
                    class="theme-panel__slider"
                    :model-value="activeThemeFontSize"
                    :min="13"
                    :max="16"
                    :step="1"
                    :marks="themeFontSizeSliderMarks"
                    :show-tooltip="false"
                    @input="handleThemeFontSizeChange"
                    @change="handleThemeFontSizeChange"
                  />
                  <el-tooltip content="重置字号" placement="top">
                    <el-button
                      class="theme-panel__reset-button"
                      text
                      circle
                      aria-label="重置字号"
                      @click="handleResetThemeFontSize"
                    >
                      <el-icon><Refresh /></el-icon>
                    </el-button>
                  </el-tooltip>
                </div>
              </div>
              <div class="theme-panel__slider-field">
                <div class="theme-panel__slider-header">
                  <span>字重</span>
                  <span>{{ activeThemeFontWeight }}</span>
                </div>
                <div class="theme-panel__metric-control">
                  <el-slider
                    class="theme-panel__slider"
                    :model-value="activeThemeFontWeight"
                    :min="400"
                    :max="700"
                    :step="100"
                    :marks="themeFontWeightSliderMarks"
                    :show-tooltip="false"
                    @input="handleThemeFontWeightChange"
                    @change="handleThemeFontWeightChange"
                  />
                  <el-tooltip content="重置字重" placement="top">
                    <el-button
                      class="theme-panel__reset-button"
                      text
                      circle
                      aria-label="重置字重"
                      @click="handleResetThemeFontWeight"
                    >
                      <el-icon><Refresh /></el-icon>
                    </el-button>
                  </el-tooltip>
                </div>
              </div>
            </section>
          </div>

          <div class="theme-panel__column theme-panel__column--side">
            <section class="theme-panel__section">
              <div class="theme-panel__header">
                <span class="theme-panel__label-group">
                  <span class="theme-panel__label">紧凑性</span>
                  <el-tooltip :content="activeBusinessDensityAllLinked ? '全部取消联动' : '全部联动全局紧凑性'" placement="top">
                    <el-button
                      class="theme-panel__link-button theme-panel__link-button--inline"
                      :class="{ 'is-unlinked': !activeBusinessDensityAllLinked }"
                      text
                      circle
                      :aria-label="activeBusinessDensityAllLinked ? '全部取消紧凑性联动' : '全部联动紧凑性'"
                      @click="handleToggleAllBusinessDensityLinked"
                    >
                      <el-icon><Link /></el-icon>
                    </el-button>
                  </el-tooltip>
                </span>
                <span class="theme-panel__value">{{ activeThemeDensityTitle }}</span>
              </div>
              <div class="theme-panel__slider-field">
                <div class="theme-panel__slider-header">
                  <span>界面密度</span>
                  <span>{{ activeThemeDensityDescription }}</span>
                </div>
                <el-slider
                  class="theme-panel__slider"
                  :model-value="activeThemeDensityScale"
                  :min="0"
                  :max="2"
                  :step="1"
                  :marks="themeDensitySliderMarks"
                  :show-tooltip="false"
                  @input="handleThemeDensityScaleChange"
                  @change="handleThemeDensityScaleChange"
                />
              </div>
            </section>

            <section class="theme-panel__section">
              <div class="theme-panel__header">
                <span class="theme-panel__label">营业视图</span>
                <span class="theme-panel__value">{{ activeBusinessDisplaySummary }}</span>
              </div>
              <div class="theme-panel__preference-row">
                <div>
                  <span>全部桌台</span>
                </div>
                <el-switch
                  :model-value="activeBusinessAllCategoryFlatMode"
                  inline-prompt
                  inactive-text="分组"
                  active-text="平铺"
                  @change="handleBusinessAllCategoryFlatModeChange"
                />
              </div>
              <div class="theme-panel__preference-row theme-panel__preference-row--stacked">
                <div>
                  <span>桌台密度</span>
                </div>
                <div class="theme-panel__preference-actions">
                  <el-tooltip :content="activeBusinessTableCardDensityLinked ? '已联动全局紧凑性' : '未联动全局紧凑性'" placement="top">
                    <el-button
                      class="theme-panel__link-button"
                      :class="{ 'is-unlinked': !activeBusinessTableCardDensityLinked }"
                      text
                      circle
                      :aria-label="activeBusinessTableCardDensityLinked ? '断开桌台密度联动' : '联动桌台密度'"
                      @click="handleToggleBusinessTableCardDensityLinked"
                    >
                      <el-icon><Link /></el-icon>
                    </el-button>
                  </el-tooltip>
                  <el-radio-group
                    class="theme-panel__compact-radio-group"
                    :model-value="activeBusinessTableCardDensity"
                    :disabled="activeBusinessTableCardDensityLinked"
                    size="small"
                    @change="handleBusinessTableCardDensityChange"
                  >
                    <el-radio-button
                      v-for="item in businessTableCardDensityList"
                      :key="item.name"
                      :value="item.name"
                    >
                      {{ item.title }}
                    </el-radio-button>
                  </el-radio-group>
                </div>
              </div>
              <div class="theme-panel__preference-row theme-panel__preference-row--stacked">
                <div>
                  <span>点单商品</span>
                </div>
                <div class="theme-panel__preference-actions">
                  <el-tooltip :content="activeBusinessProductCardDensityLinked ? '已联动全局紧凑性' : '未联动全局紧凑性'" placement="top">
                    <el-button
                      class="theme-panel__link-button"
                      :class="{ 'is-unlinked': !activeBusinessProductCardDensityLinked }"
                      text
                      circle
                      :aria-label="activeBusinessProductCardDensityLinked ? '断开点单商品密度联动' : '联动点单商品密度'"
                      @click="handleToggleBusinessProductCardDensityLinked"
                    >
                      <el-icon><Link /></el-icon>
                    </el-button>
                  </el-tooltip>
                  <el-radio-group
                    class="theme-panel__compact-radio-group"
                    :model-value="activeBusinessProductCardViewMode"
                    :disabled="activeBusinessProductCardDensityLinked"
                    size="small"
                    @change="handleBusinessProductCardViewModeChange"
                  >
                    <el-radio-button
                      v-for="item in businessProductCardViewModeList"
                      :key="item.name"
                      :value="item.name"
                    >
                      {{ item.title }}
                    </el-radio-button>
                  </el-radio-group>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </el-popover>
  </span>
</template>

<style scoped lang="scss">
.theme-switch-root {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.theme-switch-button {
  position: relative;
  display: inline-flex;
  flex: 0 0 var(--tea-size-control-lg);
  align-items: center;
  justify-content: center;
  width: var(--tea-size-control-lg);
  min-width: var(--tea-size-control-lg);
  max-width: var(--tea-size-control-lg);
  height: var(--tea-size-control-lg);
  min-height: var(--tea-size-control-lg);
  max-height: var(--tea-size-control-lg);
  aspect-ratio: 1;
  padding: 0 !important;
  overflow: hidden;
  line-height: 1;
  color: var(--bussiness-sidebar-text-color);
  border-radius: var(--tea-radius-round) !important;

  &:hover,
  &:focus-visible {
    color: var(--tea-color-primary);
    background-color: var(--tea-color-fill-subtle);
  }
}

.theme-switch-button__swatch {
  position: absolute;
  right: var(--tea-component-theme-switch-button-swatch-offset);
  bottom: var(--tea-component-theme-switch-button-swatch-offset);
  width: var(--tea-component-theme-switch-button-swatch-size);
  height: var(--tea-component-theme-switch-button-swatch-size);
  border: 1px solid var(--tea-color-bg-surface);
  border-radius: var(--tea-radius-round);
  box-shadow: var(--tea-shadow-xs);
}

:global(.theme-switch-popover) {
  box-sizing: border-box;
  width: min(760px, calc(100vw - 24px)) !important;
  max-width: calc(100vw - 24px);
  overflow: hidden;
}

.theme-panel {
  --theme-panel-text-primary: var(--tea-component-theme-panel-title-text-color);
  --theme-panel-text-label: var(--tea-component-theme-panel-label-text-color);
  --theme-panel-text-regular: var(--tea-component-theme-panel-body-text-color);
  --theme-panel-text-caption: var(--tea-component-theme-panel-caption-text-color);
  --theme-panel-text-disabled: var(--tea-component-theme-panel-disabled-text-color);
  --theme-panel-text-accent: var(--tea-component-theme-panel-accent-text-color);
  --theme-panel-text-on-active: var(--tea-component-theme-panel-active-text-color);
  --theme-panel-text-on-active-muted: var(--tea-component-theme-panel-active-caption-text-color);
  --theme-panel-icon-accent: var(--tea-color-primary-dark-2);

  display: flex;
  flex-direction: column;
  gap: var(--tea-component-theme-panel-section-gap);
  min-width: 0;
  max-height: min(76vh, 720px);
  overflow: hidden auto;
  color: var(--theme-panel-text-regular);
}

.theme-panel__titlebar {
  display: flex;
  gap: var(--tea-spacing-md);
  align-items: center;
  justify-content: space-between;
  padding-bottom: var(--tea-spacing-sm);
  border-bottom: 1px solid var(--tea-color-border-subtle);

  :deep(.el-button) {
    display: inline-flex;
    gap: var(--tea-spacing-xxs);
    align-items: center;
    margin-left: 0;
    color: var(--theme-panel-text-regular);

    &:hover,
    &:focus-visible {
      color: var(--theme-panel-text-accent);
    }
  }
}

.theme-panel__title {
  font-size: var(--tea-font-size-body-lg);
  font-weight: var(--tea-font-weight-semibold);
  line-height: var(--tea-line-height-normal);
  color: var(--theme-panel-text-primary);
}

.theme-panel__section {
  display: flex;
  flex-direction: column;
  gap: var(--tea-component-theme-panel-section-gap);
  min-width: 0;
  padding: var(--tea-component-theme-panel-section-padding);
  background: var(--tea-component-theme-panel-section-bg-color);
  border: 1px solid var(--tea-component-theme-panel-section-border-color);
  border-radius: var(--tea-component-theme-panel-section-radius);
}

.theme-panel__columns {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(280px, 0.88fr);
  gap: var(--tea-component-theme-panel-column-gap);
  align-items: stretch;
}

.theme-panel__column {
  display: flex;
  flex-direction: column;
  gap: var(--tea-component-theme-panel-section-gap);
  min-width: 0;
}

.theme-panel__column--side {
  padding-left: var(--tea-component-theme-panel-column-gap);
  border-left: 1px solid var(--tea-color-border-subtle);
}

.theme-panel__header {
  display: flex;
  gap: var(--tea-spacing-sm);
  align-items: center;
  justify-content: space-between;
  min-width: 0;
}

.theme-panel__header--color {
  gap: var(--tea-spacing-xs);
}

.theme-panel__color-row {
  display: grid;
  grid-template-columns: var(--tea-component-theme-panel-color-picker-size) 1fr;
  gap: var(--tea-spacing-sm);
  align-items: start;
}

.theme-panel__label {
  flex: 0 0 auto;
  font-size: var(--tea-font-size-body-md);
  font-weight: var(--tea-font-weight-medium);
  line-height: var(--tea-line-height-normal);
  color: var(--theme-panel-text-label);
}

.theme-panel__label-group {
  display: inline-flex;
  gap: var(--tea-spacing-xs);
  align-items: center;
  min-width: 0;
}

.theme-panel__value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: var(--tea-font-size-caption);
  font-weight: var(--tea-font-weight-medium);
  line-height: var(--tea-line-height-normal);
  color: var(--theme-panel-text-caption);
  white-space: nowrap;
}

.theme-panel__segmented-group {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--tea-spacing-xs);

  :deep(.el-radio-button__inner) {
    display: flex;
    justify-content: center;
    width: 100%;
    color: var(--theme-panel-text-regular);
    border-left: var(--el-border);
    border-radius: var(--tea-radius-md);
  }

  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
    font-weight: var(--tea-font-weight-semibold);
    color: var(--theme-panel-text-on-active);
  }
}

.theme-panel__mode {
  display: inline-flex;
  gap: var(--tea-spacing-xxs);
  align-items: center;
}

.theme-panel__profile-group {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--tea-spacing-xs);

  :deep(.el-radio-button__inner) {
    display: flex;
    justify-content: center;
    width: 100%;
    min-height: var(--tea-component-theme-panel-compact-control-min-height);
    padding: var(--tea-component-theme-panel-compact-control-padding);
    color: var(--theme-panel-text-regular);
    border-left: var(--el-border);
    border-radius: var(--tea-radius-md);
  }

  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
    color: var(--theme-panel-text-on-active);
  }
}

.theme-panel__profile-option {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  font-size: var(--tea-font-size-body-sm);
  font-weight: var(--tea-font-weight-medium);
  line-height: var(--tea-line-height-normal);
  white-space: normal;
}

.theme-panel__header-actions {
  display: inline-flex;
  flex: 0 0 auto;
  gap: var(--tea-spacing-xs);
  align-items: center;
  margin-left: auto;
}

.theme-panel__performance-checkbox,
.theme-panel__advanced-checkbox {
  --el-checkbox-input-height: 13px;
  --el-checkbox-input-width: 13px;

  flex: 0 0 auto;
  height: 24px;
  margin-left: 0;
  color: var(--theme-panel-text-caption);

  :deep(.el-checkbox__label) {
    padding-left: var(--tea-spacing-3xs);
    font-size: var(--tea-font-size-caption);
    font-weight: var(--tea-font-weight-regular);
    line-height: var(--tea-line-height-compact);
    color: var(--theme-panel-text-caption);
  }

  :deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
    color: var(--theme-panel-text-accent);
  }
}

.theme-panel__surface-group {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--tea-spacing-xs);

  :deep(.el-radio-button__inner) {
    display: flex;
    justify-content: center;
    width: 100%;
    min-height: var(--tea-component-theme-panel-compact-control-min-height);
    padding: var(--tea-component-theme-panel-compact-control-padding);
    color: var(--theme-panel-text-regular);
    border-left: var(--el-border);
    border-radius: var(--tea-radius-md);
  }

  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
    color: var(--theme-panel-text-on-active);
  }

  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner .theme-panel__surface-option span:last-child) {
    color: var(--theme-panel-text-on-active-muted);
  }
}

.theme-panel__surface-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  line-height: var(--tea-line-height-compact);
  white-space: normal;

  span:first-child {
    font-size: var(--tea-font-size-body-sm);
    font-weight: var(--tea-font-weight-medium);
    color: inherit;
  }

  span:last-child {
    font-size: var(--tea-font-size-caption);
    color: var(--theme-panel-text-caption);
  }
}

.theme-panel__section--profile,
.theme-panel__section--brightness,
.theme-panel__section--surface,
.theme-panel__section--font {
  gap: var(--tea-component-theme-panel-compact-section-gap);
}

.theme-panel__section--brightness {
  gap: var(--tea-spacing-xs);
  padding-block: var(--tea-spacing-sm);
}

.theme-panel__brightness-switch {
  flex: 0 0 auto;
}

.theme-panel__brightness-control {
  min-width: 0;
  padding: 0 var(--tea-spacing-xs);

  .theme-panel__slider {
    :deep(.el-slider__runway) {
      margin: 10px 0;
    }
  }
}

.theme-panel__section--font {
  .theme-panel__slider-field {
    gap: var(--tea-component-theme-panel-compact-slider-field-gap);
    padding-bottom: 0;
  }

  .theme-panel__slider {
    :deep(.el-slider__runway) {
      margin: var(--tea-component-theme-panel-compact-slider-runway-margin);
    }

    :deep(.el-slider__marks-text) {
      margin-top: var(--tea-component-theme-panel-compact-slider-mark-margin-top);
    }
  }
}

.theme-panel__preference-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--tea-component-theme-panel-preference-gap);
  align-items: center;
  min-height: var(--tea-size-control-lg);

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: var(--tea-spacing-3xs);
    min-width: 0;
  }

  span {
    font-size: var(--tea-font-size-body-sm);
    font-weight: var(--tea-font-weight-medium);
    line-height: var(--tea-line-height-compact);
    color: var(--theme-panel-text-primary);
  }

  .theme-panel__value {
    font-size: var(--tea-font-size-caption);
    color: var(--theme-panel-text-caption);
  }

}

.theme-panel__preference-row--stacked {
  grid-template-columns: minmax(0, 1fr);
  align-items: stretch;
}

.theme-panel__preference-actions {
  display: flex;
  gap: var(--tea-spacing-xs);
  align-items: center;
  min-width: 0;
  padding-right: 2px;
}

.theme-panel__metric-control {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 24px;
  gap: var(--tea-spacing-xs);
  align-items: center;
  min-width: 0;
}

.theme-panel__reset-button {
  flex: 0 0 24px;
  width: 24px;
  min-width: 24px;
  height: 24px;
  min-height: 24px;
  padding: 0 !important;
  color: var(--theme-panel-text-caption);
  border-radius: var(--tea-radius-round);

  &:hover,
  &:focus-visible {
    color: var(--theme-panel-text-accent);
    background: var(--tea-color-fill-subtle);
  }

  &.is-disabled,
  &:disabled {
    color: var(--theme-panel-text-disabled);
    background: transparent;
    opacity: 0.72;
  }

  :deep(.el-icon) {
    font-size: 13px;
  }
}

.theme-panel__link-button {
  position: relative;
  flex: 0 0 var(--tea-size-control-md);
  width: var(--tea-size-control-md);
  height: var(--tea-size-control-md);
  color: var(--theme-panel-icon-accent);
  background: var(--tea-color-primary-subtle);
  border-radius: var(--tea-radius-round);

  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 18px;
    height: 2px;
    content: "";
    background: currentcolor;
    border-radius: var(--tea-radius-round);
    opacity: 0;
    transform: translate(-50%, -50%) rotate(-42deg) scaleX(0.6);
    transition: opacity var(--tea-motion-duration-fast) var(--tea-motion-easing-standard),
      transform var(--tea-motion-duration-fast) var(--tea-motion-easing-standard);
  }

  &.is-unlinked {
    color: var(--theme-panel-text-caption);
    background: var(--tea-color-fill-muted);

    &::after {
      opacity: 1;
      transform: translate(-50%, -50%) rotate(-42deg) scaleX(1);
    }
  }
}

.theme-panel__link-button--inline {
  flex-basis: 24px;
  width: 24px;
  height: 24px;

  &::after {
    width: 15px;
  }
}

.theme-panel__compact-radio-group {
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: repeat(3, minmax(52px, 1fr));
  width: 100%;
  min-width: 0;

  :deep(.el-radio-button__inner) {
    display: flex;
    justify-content: center;
    width: 100%;
    color: var(--theme-panel-text-regular);
    border-left: var(--el-border);
  }

  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
    font-weight: var(--tea-font-weight-semibold);
    color: var(--theme-panel-text-on-active);
  }

  :deep(.el-radio-button__original-radio:disabled + .el-radio-button__inner) {
    color: var(--theme-panel-text-disabled);
    cursor: not-allowed;
    background: var(--tea-color-fill-subtle);
    border-color: var(--tea-color-border-subtle);
    opacity: 1;
  }

  :deep(.el-radio-button__original-radio:checked:disabled + .el-radio-button__inner) {
    color: var(--theme-panel-text-accent);
    background: var(--tea-color-primary-subtle);
    border-color: var(--tea-color-primary-200);
  }
}

.theme-panel__slider-field {
  display: flex;
  flex-direction: column;
  gap: var(--tea-component-theme-panel-field-gap);
  padding-bottom: var(--tea-component-theme-panel-field-gap);
}

.theme-panel__slider-header {
  display: flex;
  gap: var(--tea-spacing-sm);
  align-items: center;
  justify-content: space-between;
  font-size: var(--tea-font-size-caption);
  line-height: var(--tea-line-height-normal);
  color: var(--theme-panel-text-caption);

  span:last-child {
    font-weight: var(--tea-font-weight-medium);
    color: var(--theme-panel-text-primary);
  }
}

.theme-panel__slider {
  --el-slider-main-bg-color: var(--tea-color-primary);
  --el-slider-runway-bg-color: var(--tea-color-fill-muted);
  --el-slider-stop-bg-color: var(--tea-color-bg-elevated);
  --el-slider-button-size: 16px;
  --el-slider-button-wrapper-size: 28px;
  --el-slider-button-wrapper-offset: -12px;

  box-sizing: border-box;
  padding: 0 var(--tea-component-theme-panel-slider-inline-padding);

  :deep(.el-slider__runway) {
    height: 6px;
    margin: var(--tea-component-theme-panel-slider-runway-margin);
    border-radius: var(--tea-radius-round);
  }

  :deep(.el-slider__bar) {
    height: 6px;
    border-radius: var(--tea-radius-round);
  }

  :deep(.el-slider__button) {
    border-color: var(--tea-color-primary);
    box-shadow: var(--tea-shadow-xs);
  }

  :deep(.el-slider__stop) {
    top: -3px;
    width: 12px;
    height: 12px;
    border: 2px solid var(--tea-color-primary);
    box-shadow: 0 0 0 2px var(--tea-color-bg-elevated);
  }

  :deep(.el-slider__marks-text) {
    margin-top: var(--tea-component-theme-panel-slider-mark-margin-top);
    font-size: var(--tea-font-size-caption);
    font-weight: var(--tea-font-weight-medium);
    line-height: var(--tea-line-height-normal);
    color: var(--theme-panel-text-caption);
    white-space: nowrap;
  }
}

.theme-panel__color-preview {
  display: grid;
  grid-template-columns: repeat(3, var(--tea-component-theme-panel-preset-width));
  gap: var(--tea-component-theme-panel-preset-row-gap)
    var(--tea-component-theme-panel-preset-column-gap);
  justify-content: start;
}

.theme-panel__color-picker {
  display: inline-flex;
  width: var(--tea-component-theme-panel-color-picker-size) !important;
  min-width: var(--tea-component-theme-panel-color-picker-size);
  height: var(--tea-component-theme-panel-color-picker-size) !important;
  line-height: var(--tea-component-theme-panel-color-picker-size);

  :deep(.el-color-picker__trigger) {
    box-sizing: border-box;
    width: var(--tea-component-theme-panel-color-picker-size) !important;
    height: var(--tea-component-theme-panel-color-picker-size) !important;
    aspect-ratio: 1;
    padding: var(--tea-component-theme-panel-color-picker-padding) !important;
    border-radius: var(--tea-radius-md);
  }

  :deep(.el-color-picker__color) {
    display: block;
    width: 100% !important;
    min-width: 0;
    height: 100% !important;
    min-height: 0;
    border-radius: var(--tea-radius-sm);
  }

  :deep(.el-color-picker__color-inner) {
    width: 100%;
    height: 100%;
    border-radius: inherit;
  }
}

.theme-panel__preset {
  display: flex;
  flex-direction: column;
  gap: var(--tea-component-theme-panel-preset-item-gap);
  align-items: center;
  justify-content: flex-start;
  min-width: var(--tea-component-theme-panel-preset-width);
  padding: 0;
  cursor: pointer;
  background: transparent;
  border: 0;
}

.theme-panel__preset-swatch {
  width: var(--tea-component-theme-panel-preset-swatch-size);
  height: var(--tea-component-theme-panel-preset-swatch-size);
  border: 1px solid var(--tea-color-border);
  border-radius: var(--tea-radius-round);
}

.theme-panel__preset-name {
  font-size: var(--tea-font-size-caption);
  font-weight: var(--tea-font-weight-medium);
  line-height: var(--tea-line-height-compact);
  color: var(--theme-panel-text-caption);
  white-space: nowrap;
}

.theme-panel__preset.is-active {
  .theme-panel__preset-swatch {
    outline: 2px solid var(--tea-color-primary);
    outline-offset: 2px;
  }

  .theme-panel__preset-name {
    font-weight: var(--tea-font-weight-semibold);
    color: var(--theme-panel-text-accent);
  }
}

.theme-panel__scale {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  height: var(--tea-component-theme-panel-scale-height);
  overflow: hidden;
  border-radius: var(--tea-radius-round);
}

.theme-panel__scale--inline {
  flex: 1 1 auto;
  min-width: 72px;
}

.theme-panel__scale-item--page {
  background: var(--tea-color-bg-page);
}

.theme-panel__scale-item--surface {
  background: var(--tea-color-bg-surface);
}

.theme-panel__scale-item--soft {
  background: var(--tea-color-primary-subtle);
}

.theme-panel__scale-item--main {
  background: var(--tea-color-primary);
}

.theme-panel__scale-item--hover {
  background: var(--tea-color-primary-hover);
}

.theme-panel__scale-item--active {
  background: var(--tea-color-primary-active);
}

@media (width <= 860px) {
  .theme-panel__columns {
    grid-template-columns: minmax(0, 1fr);
  }

  .theme-panel__column--side {
    padding-top: var(--tea-component-theme-panel-section-gap);
    padding-left: 0;
    border-top: 1px solid var(--tea-color-border-subtle);
    border-left: 0;
  }
}
</style>
